import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, PlusCircleIcon, ChevronDownIcon, EllipsisVerticalIcon, LockClosedIcon, CheckIcon, QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import CurrentUserAvatar from '../../ui/CurrentUserAvatar';
import { SchoolCard } from '../../shared/SchoolCard';
import { useSchool } from '../../../contexts/SchoolContext';
import schoolService from '../../../services/schoolService';
import { getSelectableSchoolIds, switchActiveSchool } from '../../../services/activeSchoolService';
import useDeviceType from '../../../hooks/useDeviceType';

const UserAccountMenu = ({
  user,
  onLogout,
  currentRole,
  canSwitchRoles,
  availableRoles,
  switchRole,
  currentPermissions
}) => {
  const { schoolId, school } = useSchool();
  const deviceType = useDeviceType();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [activeMenuSchoolId, setActiveMenuSchoolId] = useState(null);
  const [schoolSelectionError, setSchoolSelectionError] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(null); // Track selection immediately

  // Sync selectedSchoolId with actual schoolId when it changes
  useEffect(() => {
    setSelectedSchoolId(schoolId || null);
  }, [schoolId]);

  // Fetch user's schools
  useEffect(() => {
    const fetchSchools = async () => {
      if (!user) return;

      setLoadingSchools(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const schoolIds = getSelectableSchoolIds(userDoc.data());

        const mySchools = await Promise.all(
          schoolIds.map(id => schoolService.getSchool(id))
        );
        setSchools(mySchools.filter(Boolean));
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchSchools();
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenuSchoolId && !event.target.closest('.menu-button') && !event.target.closest('.menu-dropdown')) {
        setActiveMenuSchoolId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenuSchoolId]);

  // Close main dropdown when clicking outside (desktop only)
  useEffect(() => {
    // Skip on mobile - mobile modal has its own close button
    if (deviceType.isMobile) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, deviceType.isMobile]);

  const handleSelectSchool = async (school) => {
    if (!user || school.id === schoolId) return;

    // Provide immediate visual feedback
    setSelectedSchoolId(school.id);
    setSchoolSelectionError(null);

    try {
      await switchActiveSchool(school.id);
    } catch (error) {
      console.error('Error switching school:', error);
      setSelectedSchoolId(schoolId || null);
      setSchoolSelectionError('Swan could not verify access to that school. Try again or contact support.');
    }
  };

  if (!user) return null;

  // Menu content component (reusable for both desktop and mobile)
  const MenuContent = () => (
    <div className="menu p-2 w-full">
        <li className="menu-title px-3 py-2">
          <span className="text-base-content/70 font-semibold">Account Menu</span>
        </li>

        {/* Role Switcher */}
        {canSwitchRoles && currentRole && availableRoles && (
          <>
            <div className="px-3 py-2">
              <div className="join w-full">
                {availableRoles.map((role) => {
                  const roleLabels = {
                    teacher: 'Teacher',
                    admin: 'Admin',
                    parent: 'Parent',
                    dev: 'Dev'
                  };

                  const roleInitials = {
                    teacher: 'T',
                    admin: 'A',
                    parent: 'P',
                    dev: 'D'
                  };

                  // Use initials for 3+ roles, full labels for 2 roles
                  const displayText = availableRoles.length >= 3
                    ? roleInitials[role]
                    : roleLabels[role];

                  return (
                    <button
                      key={role}
                      onClick={() => switchRole?.(role)}
                      aria-label={`Switch to ${roleLabels[role]} role`}
                      aria-pressed={role === currentRole}
                      className={`btn join-item flex-1 ${
                        !deviceType.isMobile ? 'tooltip' : ''
                      } ${
                        role === currentRole
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                      data-tip={!deviceType.isMobile ? roleLabels[role] : undefined}
                    >
                      {displayText}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="divider my-1"></div>
          </>
        )}

        {/* School Selection Accordion */}
        <div className="px-2 py-1 w-full">
          {school ? (
            <div
              className="p-2 rounded-lg cursor-pointer ring-1 ring-primary transition-all duration-200 hover:bg-base-200 max-w-full"
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            >
              <div className="flex items-center gap-2 min-w-0">
                {/* School Icon */}
                <div className="flex items-center justify-center rounded-lg bg-base-200 p-1 flex-shrink-0">
                  <BuildingOfficeIcon className="w-6 h-6 text-primary" />
                </div>

                {/* School Name */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base-content truncate text-sm">
                    {school.name}
                  </div>
                </div>

                {/* Chevron */}
                <ChevronDownIcon
                  className={`w-4 h-4 text-base-content/70 transition-transform duration-200 flex-shrink-0 ${
                    isAccordionOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>
          ) : (
            <div
              className="p-2 rounded-lg border border-base-300 hover:bg-base-200 cursor-pointer transition-colors"
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="w-5 h-5 text-base-content/40" />
                  <span className="text-sm text-base-content/60">No school selected</span>
                </div>
                <ChevronDownIcon
                  className={`w-4 h-4 text-base-content/70 transition-transform duration-200 ${
                    isAccordionOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>
          )}

          {/* Accordion Content */}
          {isAccordionOpen && (
            <div className="mt-2 space-y-1">
              {schoolSelectionError && (
                <div className="alert alert-error mx-2 py-2 text-xs" role="alert">
                  {schoolSelectionError}
                </div>
              )}
              {/* Schools List - Scrollable */}
              <div className="max-h-48 overflow-y-auto overflow-x-hidden px-2 py-1 space-y-1">
                {loadingSchools ? (
                  <div className="flex items-center justify-center py-4">
                    <span className="loading loading-spinner loading-sm"></span>
                  </div>
                ) : schools.length > 0 ? (
                  schools.map((school) => {
                    // Use selectedSchoolId for immediate feedback, fall back to actual schoolId
                    const isActive = school.id === (selectedSchoolId || schoolId);

                    return (
                      <div
                        key={school.id}
                        className="flex items-center gap-1 group relative min-w-0"
                      >
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <SchoolCard
                            school={school}
                            active={false}  // Don't show checkmark inside card
                            onClick={() => handleSelectSchool(school)}
                            variant="compact"
                            enableProfileNavigation={false}
                          />
                        </div>

                        {/* Active indicator - show our own checkmark */}
                        {isActive && (
                          <CheckIcon className="w-5 h-5 text-primary flex-shrink-0 mr-1" />
                        )}

                        {/* Options Menu Button - Always visible for all schools */}
                        <div className="relative flex-shrink-0">
                          <button
                            className="btn btn-ghost btn-circle btn-xs opacity-60 hover:opacity-100 menu-button"
                            aria-label={`School options for ${school.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuSchoolId(activeMenuSchoolId === school.id ? null : school.id);
                            }}
                          >
                            <EllipsisVerticalIcon className="w-4 h-4" />
                          </button>

                          {/* Dropdown Menu */}
                          {activeMenuSchoolId === school.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-base-100 rounded-lg shadow-lg border border-base-300 z-[100] menu-dropdown">
                              <ul className="menu menu-compact p-2">
                                <li>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMenuSchoolId(null);
                                      setIsOpen(false);
                                      navigate('/help');
                                    }}
                                    className="text-left"
                                  >
                                    <LockClosedIcon className="w-4 h-4" />
                                    <span>
                                      <span className="block">Leave School</span>
                                      <span className="block text-xs text-base-content/55">Contact Swan support</span>
                                    </span>
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : null}
              </div>

              {/* Add School Button */}
              <button
                onClick={() => { setIsOpen(false); navigate('/join-bulk'); }}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-base-200 text-sm text-primary transition-colors w-full text-left"
              >
                <PlusCircleIcon className="w-4 h-4" />
                <span>Add School</span>
              </button>
            </div>
          )}
        </div>

        <div className="divider my-1 max-w-full"></div>

        {/* Profile Links - role-aware */}
        {currentRole === 'parent' ? (
          <li>
            <a href="/mychildren" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>My Family</span>
            </a>
          </li>
        ) : (
          <>
            {school && (
              <li>
                <a href="/school-profile" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
                  <BuildingOfficeIcon className="w-5 h-5" />
                  <span>School Profile</span>
                </a>
              </li>
            )}
            <li>
              <a href={`/teacher/${user.uid}`} className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>My Teacher Profile</span>
              </a>
            </li>
          </>
        )}
        <li>
          <a href="/settings" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Settings</span>
          </a>
        </li>
        <li>
          <a href="/help" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
            <QuestionMarkCircleIcon className="w-5 h-5" />
            <span>Help Center</span>
          </a>
        </li>
        {currentPermissions?.canManageUsers && (
          <li>
            <a href="/role-management" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-5.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <span>Role Management</span>
            </a>
          </li>
        )}
        {currentPermissions?.canSwitchRoles && (
          <>
            <li>
              <a href="/developer/test-environments" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
                <BuildingOfficeIcon className="w-5 h-5" />
                <span>Test Environments</span>
              </a>
            </li>
            <li>
              <a href="/dev-settings" className="flex items-center space-x-3 px-3 py-3 hover:bg-base-200">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Dev Settings</span>
              </a>
            </li>
          </>
        )}

        {/* Logout */}
        <div className="divider my-2"></div>
        <li>
          <button
            onClick={onLogout}
            className="flex items-center space-x-3 px-3 py-3 text-error hover:bg-error/10 w-full text-left"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Sign Out</span>
          </button>
        </li>
    </div>
  );

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Avatar Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="btn btn-ghost btn-circle hover:bg-base-200"
          aria-label="Open account menu"
          aria-expanded={isOpen}
        >
          <CurrentUserAvatar size="small" />
        </button>

        {/* Desktop Dropdown */}
        {isOpen && !deviceType.isMobile && (
          <div className="absolute right-0 mt-3 z-[60] w-64 bg-base-100 rounded-box shadow-xl border border-base-300">
            <MenuContent />
          </div>
        )}
      </div>

      {/* Mobile Modal — z-[60] sits above the bottom dock (z-50, MobileDock.jsx) so the
          full sheet (incl. Sign Out) is reachable rather than hidden behind it. */}
      {isOpen && deviceType.isMobile && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-end">
          <div
            className="bg-base-100 w-screen max-w-none rounded-t-2xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{ marginLeft: 0, marginRight: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300">
              <h3 className="text-lg font-semibold">Account</h3>
              <button
                className="btn btn-circle btn-sm btn-ghost"
                onClick={() => setIsOpen(false)}
                aria-label="Close account menu"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable Content — bottom padding clears the iOS home indicator
                so the last item (Sign Out) is never cut off at the screen edge. */}
            <div
              className="overflow-y-auto flex-1 [&::-webkit-scrollbar]:hidden"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)',
              }}
            >
              <MenuContent />
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default UserAccountMenu;
